import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import NavigationBar from '../NavigationBar';
import Footer from '../Footer';
import './Instructions.css';

const Instructions = () => {
  const { state } = useAuth();
  
  return (
    <>
      <NavigationBar />
      
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 hero-content">
              <h1>Secure Your Paper with AES-256 Encryption</h1>
              <p className="lead">
                SecurePaper transforms your sensitive written information into encrypted SecurePaper codes, 
                making your paper documents impossible to read without the proper key.
              </p>
              <div className="hero-buttons">
                <Link to="/create_encrypted_text" className="btn btn-primary btn-lg">
                  <i className="fa fa-lock" aria-hidden="true"></i> Create SecurePaper
                </Link>
                {state.isLoggedIn && (
                  <>
                    <Link to="/decrypt" className="btn btn-secondary">
                      <i className="fa fa-qrcode" aria-hidden="true"></i> Scan SecurePaper Code
                    </Link>
                    <Link to="/messages" className="btn btn-outline-primary btn-lg">
                      View My Papers
                    </Link>
                  </>
                )}
              </div>
            </div>
            <div className="col-lg-6 hero-image">
              <div className="encryption-illustration">
                <i className="fa fa-file-lines main-icon" aria-hidden="true"></i>
                <i className="fa fa-qrcode icon-element" aria-hidden="true"></i>
                <i className="fa fa-key icon-element" aria-hidden="true"></i>
                <i className="fa fa-lock icon-element" aria-hidden="true"></i>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <h2 className="section-title text-center">Why Choose SecurePaper?</h2>
          
          <div className="row features-row">
            <div className="col-md-4">
              <div className="feature-card">
                <div className="feature-icon">
                  <i className="fa fa-shield-alt" aria-hidden="true"></i>
                </div>
                <h3>Protect Written Information</h3>
                <p>Convert sensitive written notes into encrypted SecurePaper codes that can only be deciphered with your unique key.</p>
              </div>
            </div>
            
            <div className="col-md-4">
              <div className="feature-card">
                <div className="feature-icon">
                  <i className="fa fa-user-secret" aria-hidden="true"></i>
                </div>
                <h3>Physical Document Security</h3>
                <p>Store passwords and sensitive info on paper without the risk of exposure. Only you can unlock the real content.</p>
              </div>
            </div>
            
            <div className="col-md-4">
              <div className="feature-card">
                <div className="feature-icon">
                  <i className="fa fa-check-circle" aria-hidden="true"></i>
                </div>
                <h3>Simple Paper Backup</h3>
                <p>Create secure, printable backups of your sensitive information that remain safe even if found or stolen.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* How It Works Section */}
      <section className="how-it-works-section">
        <div className="container">
          <h2 className="section-title text-center">How It Works</h2>
          
          <div className="steps-container">
            <div className="step-item">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Enter Your Sensitive Information</h3>
                <p>Type in the content you'd normally write down insecurely—passwords, PINs, financial details, personal notes, or any confidential data.</p>
              </div>
            </div>
            
            <div className="step-item">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Print your SecurePaper</h3>
                <p>Your information is transformed into an encrypted SecurePaper code that you can print and store physically, replacing vulnerable handwritten notes.</p>
              </div>
            </div>
            
            <div className="step-item">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Retrieve When Needed</h3>
                <p>When you need to access your information, simply scan the printed SecurePaper code and enter your encryption key—much safer than plain text.</p>
              </div>
            </div>
          </div>
          
          <div className="qr-example">
            <div className="qr-code-placeholder">
              <i className="fa fa-qrcode" aria-hidden="true"></i>
            </div>
          </div>
          
          <div className="text-center">
            <Link to="/create_encrypted_text" className="btn btn-primary btn-lg">
              Secure Your Information <i className="fa fa-arrow-right" aria-hidden="true"></i>
            </Link>
            <Link to="/decrypt" className="btn btn-outline-primary btn-lg ml-3">
              <i className="fa fa-qrcode" aria-hidden="true"></i> Scan SecurePaper Code
            </Link>
          </div>
        </div>
      </section>
      
      {/* Use Cases Section */}
      <section className="use-cases-section">
        <div className="container">
          <h2 className="section-title text-center">What Can You Secure?</h2>
          
          <div className="row use-cases-row">
            <div className="col-lg-6">
              <div className="use-case-card">
                <div className="use-case-icon">
                  <i className="fa fa-credit-card" aria-hidden="true"></i>
                </div>
                <div className="use-case-content">
                  <h3>Financial Records</h3>
                  <p>Replace vulnerable handwritten bank details, account numbers, and PINs with encrypted SecurePaper codes that can be safely stored in a physical folder.</p>
                </div>
              </div>
            </div>
            
            <div className="col-lg-6">
              <div className="use-case-card">
                <div className="use-case-icon">
                  <i className="fa fa-key" aria-hidden="true"></i>
                </div>
                <div className="use-case-content">
                  <h3>Password Notebook</h3>
                  <p>Create a secure alternative to written password notebooks. Your printed SecurePaper codes are meaningless without the decryption key.</p>
                </div>
              </div>
            </div>
            
            <div className="col-lg-6">
              <div className="use-case-card">
                <div className="use-case-icon">
                  <i className="fa fa-file-alt" aria-hidden="true"></i>
                </div>
                <div className="use-case-content">
                  <h3>Confidential Documents</h3>
                  <p>Keep paper copies of contracts, legal documents, and personal records in an encrypted format that protects your privacy.</p>
                </div>
              </div>
            </div>
            
            <div className="col-lg-6">
              <div className="use-case-card">
                <div className="use-case-icon">
                  <i className="fa fa-home" aria-hidden="true"></i>
                </div>
                <div className="use-case-content">
                  <h3>Home Security Information</h3>
                  <p>Safely document alarm codes, safe combinations, and spare key locations on paper without risking home security.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="faq-section" style={{ padding: '3rem 0' }}>
        <div className="container">
          <h2 className="text-center mb-4">Frequently Asked Questions</h2>
          <div className="row justify-content-center">
            <div className="col-lg-9">
              <div className="mb-4">
                <h5><i className="fa fa-question-circle text-primary" aria-hidden="true"></i> Is my text sent to your servers?</h5>
                <p className="text-muted">No. Encryption happens entirely in your browser with AES-256. The content never leaves your device - we store at most a document title and date if you have an account. See the <Link to="/security">Security Model</Link> for details.</p>
              </div>
              <div className="mb-4">
                <h5><i className="fa fa-question-circle text-primary" aria-hidden="true"></i> What happens if I lose the encryption key?</h5>
                <p className="text-muted">The paper becomes permanently unreadable - by design, there is no backdoor. Protect yourself: export a key backup file from your <Link to="/keys">keys page</Link>, print the key QR and store it separately from the document, or use passphrase mode and remember the passphrase.</p>
              </div>
              <div className="mb-4">
                <h5><i className="fa fa-question-circle text-primary" aria-hidden="true"></i> What is the difference between a key and a passphrase?</h5>
                <p className="text-muted">A key is random and stored for you (in your browser, or in your account). A passphrase is something you remember - nothing gets stored anywhere, so the printed paper plus your memory is the complete backup. Passphrase mode is ideal for wills, emergency envelopes, and anything meant to work years from now.</p>
              </div>
              <div className="mb-4">
                <h5><i className="fa fa-question-circle text-primary" aria-hidden="true"></i> What if SecurePaper shuts down - can I still read my papers?</h5>
                <p className="text-muted">Yes. Download the <a href="/offline-decryptor.html" download>offline decryptor</a> - a single HTML file that decrypts every SecurePaper format without internet or our servers. The data format is also publicly documented, so any developer can recover your data with standard tools.</p>
              </div>
              <div className="mb-4">
                <h5><i className="fa fa-question-circle text-primary" aria-hidden="true"></i> Do I need an account?</h5>
                <p className="text-muted">No. Everything works without one; keys are then saved in your browser. An account adds cloud sync of your keys so you can decrypt from any device - a convenience trade-off explained on the <Link to="/security">Security Model</Link> page.</p>
              </div>
              <div className="mb-4">
                <h5><i className="fa fa-question-circle text-primary" aria-hidden="true"></i> How much text fits on one page?</h5>
                <p className="text-muted">Roughly 100 characters per QR code; longer documents split into multiple codes automatically and the form shows a live estimate. For long documents, consider several shorter papers - they are easier to scan back.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Secure Your Papers?</h2>
            <p>Replace vulnerable handwritten notes with encrypted SecurePaper codes — it's simple, secure, and paper-friendly.</p>
            <div className="cta-buttons">
              <Link to="/create_encrypted_text" className="btn btn-light btn-lg">
                Create SecurePaper
              </Link>
              <Link to="/decrypt" className="btn btn-light btn-lg">
                <i className="fa fa-qrcode" aria-hidden="true"></i> Scan SecurePaper Code
              </Link>
              {!state.isLoggedIn && (
                <Link to="/signup" className="btn btn-outline-light btn-lg">
                  Sign Up for Free
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </>
  );
};

export default Instructions;
